import { API_BASE_URL } from "@/lib/constants/app";

/**
 * Fast canvas type detection using backend API
 * Uses structured completion to determine if user wants code or document canvas
 */

/**
 * Detect canvas type using backend API
 * @param {string} userInput - The user's input text
 * @returns {Promise<string>} - Canvas type: 'code', 'text', 'markdown', or 'html'
 */
export async function detectCanvasTypeWithAI(userInput) {
  try {
    // Use backend API for canvas type detection

    const response = await fetch(`${API_BASE_URL}/api/ai/canvas/detect-type`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userInput,
      }),
    });

    if (!response.ok) {
      console.error("Backend API error for canvas type detection");
      return detectCanvasTypeHeuristic(userInput);
    }

    const result = await response.json();
    const detectedType = result.canvasType;

    return ["code", "text", "markdown", "html"].includes(detectedType)
      ? detectedType
      : "text";
  } catch (error) {
    console.error("Error detecting canvas type with AI:", error);
    return detectCanvasTypeHeuristic(userInput);
  }
}

/**
 * Fast heuristic-based canvas type detection
 * @param {string} userInput - The user's input text
 * @returns {string} - Canvas type: 'code', 'text', 'markdown', or 'html'
 */
export function detectCanvasTypeHeuristic(userInput) {
  if (!userInput || typeof userInput !== "string") {
    return "text";
  }

  const lowerInput = userInput.toLowerCase();

  // Code keywords with higher weight
  const codeKeywords = [
    "code",
    "function",
    "script",
    "programming",
    "debug",
    "algorithm",
    "api",
    "javascript",
    "python",
    "html",
    "css",
    "react",
    "component",
    "variable",
    "loop",
    "class",
    "method",
    "import",
    "export",
    "const",
    "let",
    "var",
    "if",
    "else",
    "for",
    "while",
    "return",
    "async",
    "await",
    "promise",
    "fetch",
    "json",
    "array",
    "object",
    "string",
    "number",
    "boolean",
    "null",
    "undefined",
    "console",
    "log",
  ];

  // Document/text keywords
  const documentKeywords = [
    "write",
    "document",
    "article",
    "essay",
    "blog",
    "content",
    "text",
    "story",
    "letter",
    "report",
    "paragraph",
    "sentence",
    "word",
    "draft",
    "compose",
    "author",
    "chapter",
    "section",
    "title",
    "heading",
    "summary",
    "conclusion",
    "introduction",
    "outline",
  ];

  // Specific format keywords
  const markdownKeywords = [
    "markdown",
    "md",
    "readme",
    "documentation",
    "docs",
  ];
  const htmlKeywords = [
    "html",
    "webpage",
    "website",
    "web page",
    "dom",
    "element",
    "tag",
  ];

  // Count keyword matches
  const codeMatches = codeKeywords.filter((keyword) =>
    lowerInput.includes(keyword)
  ).length;
  const documentMatches = documentKeywords.filter((keyword) =>
    lowerInput.includes(keyword)
  ).length;
  const markdownMatches = markdownKeywords.filter((keyword) =>
    lowerInput.includes(keyword)
  ).length;
  const htmlMatches = htmlKeywords.filter((keyword) =>
    lowerInput.includes(keyword)
  ).length;

  // Check for code patterns
  const hasCodePatterns =
    /(?:function|const|let|var|if|for|while|class|import|export|\{|\}|\(|\)|;|=>|\.js|\.py|\.html|\.css)/i.test(
      userInput
    );

  // Determine type based on matches and patterns
  if (
    htmlMatches > 0 ||
    lowerInput.includes("<") ||
    lowerInput.includes("</")
  ) {
    return "html";
  }

  if (
    markdownMatches > 0 ||
    lowerInput.includes("##") ||
    lowerInput.includes("```")
  ) {
    return "markdown";
  }

  if (codeMatches > documentMatches || hasCodePatterns) {
    return "code";
  }

  if (documentMatches > 0) {
    return "text";
  }

  // Default to text for ambiguous cases
  return "text";
}

/**
 * Detect programming language for code canvas
 * @param {string} userInput - The user's input text
 * @returns {string} - Programming language
 */
export function detectProgrammingLanguage(userInput) {
  if (!userInput || typeof userInput !== "string") {
    return "javascript";
  }

  const lowerInput = userInput.toLowerCase();

  // Language-specific keywords
  const languagePatterns = {
    python: ["python", "def ", "import ", "from ", "__name__", "print(", ".py"],
    javascript: [
      "javascript",
      "js",
      "function",
      "const ",
      "let ",
      "var ",
      "=>",
      "console.log",
      ".js",
    ],
    typescript: [
      "typescript",
      "ts",
      "interface",
      "type ",
      ":string",
      ":number",
      ".ts",
    ],
    html: ["html", "<html", "<div", "<span", "<p>", "<h1", "<head", "<body"],
    css: [
      "css",
      "style",
      "color:",
      "background:",
      "margin:",
      "padding:",
      "display:",
    ],
    json: ["json", "{", "}", '":', "null", "true", "false"],
    jsx: [
      "jsx",
      "react",
      "component",
      "props",
      "state",
      "usestate",
      "useeffect",
    ],
    tsx: ["tsx", "react", "typescript", "interface props", "fc<"],
  };

  // Check for language-specific patterns
  for (const [language, patterns] of Object.entries(languagePatterns)) {
    if (patterns.some((pattern) => lowerInput.includes(pattern))) {
      return language;
    }
  }

  // Default to JavaScript for code
  return "javascript";
}
